import * as _ from "lodash";
import * as Redux from "redux";
import { ThunkAction } from "redux-thunk";
import { AgentGroup } from "../../shared/model/AgentGroup";
import { HashMap } from "../../shared/model/HashMap";
import { ThunkResult, Utilities } from "../../shared/utilities";
import { ITriggerParameter, Process } from "../model/Process";
import { IRunOnceTriggerData } from "../model/RunOnceDialogState";
import { getAgentGroup, State } from "../model/State";

import * as FetchActions from "./FetchActions";
import { FetchId } from "./FetchActions";
import * as SelectActions from "./SelectProcessActions";

function executeRunOnce(triggerData: IRunOnceTriggerData[], agentGroupName: string): ThunkResult<Promise<boolean>, State> {
    return async (dispatch, getState) => {
        try {
            dispatch(FetchActions.fetchStart(FetchId.runOnce));
            const currentAgentGroup = getAgentGroup(getState(), agentGroupName);
            await Utilities.postJsonAndParseResponse(`api/processList/runOnce/${currentAgentGroup.environment}/${currentAgentGroup.name}`, triggerData);
            dispatch(FetchActions.fetchFinish(FetchId.runOnce));
            Utilities.toastSuccess(FRENDS.Localizations.ProcessStartMessagesSentSuccessfully);
            dispatch(SelectActions.deselectAllProcesses());
            return true;
        } catch (error) {
            dispatch(FetchActions.reportFetchError(error));
            return false;
        }
    };
}

export function runOnce(processIds: number[], agentGroupName: string): ThunkResult<void, State> {
    return async (dispatch, getState) => {
        const state = getState();
        const selectedProcesses = state.processes.filter((p) => processIds.indexOf(p.id) !== -1);
        const currentAgentGroup = getAgentGroup(getState(), agentGroupName);

        if (currentAgentGroup.isCrossPlatform) {
            const selectedFrameworkProcesses = selectedProcesses.filter(process => !process.isCrossPlatform);
            if (selectedFrameworkProcesses.length > 0) {
                const commaSeparatedFrameworkProcessList = selectedFrameworkProcesses.map(process => `"${process.name}"`).join(", ");
                const message = `Cannot run process${selectedFrameworkProcesses.length > 1 ? "es" : ""} ${commaSeparatedFrameworkProcessList} targeting NET Framework because Agent group "${currentAgentGroup.displayName}" is Cross-Platform`;
                Utilities.toastError(message);
                return;
            }
        } else {
            const selectedDotNetProcesses = selectedProcesses.filter(process => process.isDotNet);
            if (selectedDotNetProcesses.length > 0) {
                const commaSeparatedDotNetProcessList = selectedDotNetProcesses.map(process => `"${process.name}"`).join(", ");
                const message = `Cannot run process${selectedDotNetProcesses.length > 1 ? "es" : ""} ${commaSeparatedDotNetProcessList} targeting NET 8.0 because Agent group "${currentAgentGroup.displayName}" is not Cross-Platform`;
                Utilities.toastError(message);
                return;
            }
        }

        const queryParameter = selectedProcesses.map((p) => "processIds=" + p.id).join("&");
        const manualTriggerParameters = await Utilities.getFetch<HashMap<ITriggerParameter[]>>("api/processList/manualTriggerParameters?" + queryParameter);
        const objectValues = Object.keys(manualTriggerParameters).map((key) => manualTriggerParameters[key]);
        if (objectValues.every((triggerParameters) => triggerParameters.length === 0)) {
            const triggerData = processIds.map((id) => ({ id: id, triggerData: {} }));
            dispatch(executeRunOnce(triggerData, agentGroupName));
        } else {
            dispatch(showRunOnceDialog(selectedProcesses, agentGroupName, manualTriggerParameters));
        }
    };
}

export const SHOW_RUN_ONCE_DIALOG = "SHOW_RUN_ONCE_DIALOG";
function showRunOnceDialog(processes: Process[], agentGroupName: string, manualTriggerParameters: HashMap<ITriggerParameter[]>): ShowRunOnceDialogAction {
    return {
        type: SHOW_RUN_ONCE_DIALOG,
        processes: processes,
        agentGroupName: agentGroupName,
        manualTriggerParameters: manualTriggerParameters
    };
}
export interface ShowRunOnceDialogAction extends Redux.Action {
    processes: Process[];
    agentGroupName: string;
    manualTriggerParameters: HashMap<ITriggerParameter[]>;
}

export const UPDATE_RUN_ONCE_PARAMETER = "UPDATE_RUN_ONCE_PARAMETER";
export function updateRunOnceParameter(id: number, key: string, value: string): UpdateRunOnceParameterAction {
    return {
        type: UPDATE_RUN_ONCE_PARAMETER,
        id: id,
        key: key,
        value: value
    };
}

export interface UpdateRunOnceParameterAction extends Redux.Action {
    id: number;
    key: string;
    value: string;
}

export function runOnceWithParameters(): ThunkResult<void, State> {
    return async (dispatch, getState) => {
        const state = getState();
        const success = await dispatch(executeRunOnce(
            state.runOnceDialogState.runOnceProcessModel,
            state.runOnceDialogState.agentGroup));
        if (success) {
            dispatch(closeRunOnceDialog());
        }
    };
}

export const CLOSE_RUN_ONCE_DIALOG = "CLOSE_RUN_ONCE_DIALOG";
export function closeRunOnceDialog(): Redux.Action {
    return { type: CLOSE_RUN_ONCE_DIALOG };
}
